import { useCallback, useEffect, useId, useMemo, useState } from 'react';
import { api, ErrorApi } from '../lib/api.js';
import { aTexto } from '../lib/formato.js';
import { buscarOpciones, catalogo, olvidarOpciones, type Opcion } from '../lib/opciones.js';
import { Error as AvisoError } from './Estados.jsx';

/**
 * Formulario declarativo.
 *
 * Un campo se declara con su tipo y, cuando viene de catalogo, con la ruta de
 * donde salen sus opciones. Lo que el formulario hace por su cuenta:
 *
 * - Pone el label a TODO campo. Un campo sin label accesible no se puede
 *   operar con lector de pantalla, y es el defecto mas facil de cometer.
 * - Convierte el dinero: la persona captura pesos, la API recibe centavos.
 *   La conversion vive aqui porque es el unico punto donde entra dinero.
 * - Mapea los errores de validacion del servidor AL CAMPO que los produjo.
 *   Un 400 que solo dice "datos invalidos" obliga a adivinar cual de doce
 *   campos esta mal.
 *
 * Lo que NO hace: validar en vez del servidor. Lo de aqui es cortesia para
 * no mandar peticiones condenadas; la validacion que cuenta es la del
 * esquema del servidor, que es la misma fuente que genero estos tipos.
 */

export type TipoCampo =
  'texto' | 'texto-largo' | 'numero' | 'dinero' | 'fecha' | 'hora' | 'si-no' | 'lista' | 'catalogo';

export interface Campo {
  nombre: string;
  etiqueta: string;
  tipo: TipoCampo;
  /** Obligatorio. Lo marca la etiqueta y lo exige el servidor. */
  requerido?: boolean;
  ayuda?: string;
  /** Para `lista`: los valores fijos del enum, con su texto visible. */
  valores?: { valor: string; etiqueta: string }[];
  /** Para `catalogo`: la entidad de donde salen las opciones. */
  catalogo?: string;
  /** Solo se captura al dar de alta; despues no se cambia. */
  soloAlta?: boolean;
  /** Ancho completo en la rejilla de dos columnas. */
  ancho?: boolean;
  min?: number;
  max?: number;
  patron?: string;
}

interface Props {
  /** Entidad: `clientes`, `sitios`. */
  ruta: string;
  campos: Campo[];
  /** Valores actuales cuando se edita. Ausente = alta. */
  actual?: Record<string, unknown> | undefined;
  /** Llave del registro al editar: folio, clave o id. */
  llave?: string | undefined;
  /** Titulo del panel. */
  titulo: string;
  alGuardar: () => void;
  alCerrar: () => void;
  /**
   * Contenido propio del registro abierto, dentro del mismo dialogo.
   *
   * Existe porque dibujarlo FUERA no funciona: este panel es
   * `aria-modal="true"`, asi que todo lo que queda atras es inalcanzable
   * —el puntero no llega y un lector de pantalla tampoco—. Se veia, y no se
   * podia usar: la peor de las dos.
   */
  extra?: JSX.Element | undefined;
}

/** Valor inicial de un campo: lo que trae el registro, ya en pesos si es dinero. */
function valorInicial(c: Campo, actual: Record<string, unknown> | undefined): string {
  const v = actual?.[c.nombre];
  if (v === null || v === undefined) return '';
  if (c.tipo === 'dinero' && typeof v === 'number') return String(v / 100);
  if (c.tipo === 'si-no') return v ? 'si' : 'no';
  if (c.tipo === 'fecha' && typeof v === 'string') return v.slice(0, 10);
  if (c.tipo === 'hora' && typeof v === 'string') return v.slice(0, 5);
  return aTexto(v);
}

export function Formulario({
  ruta,
  campos,
  actual,
  llave,
  titulo,
  alGuardar,
  alCerrar,
  extra,
}: Props): JSX.Element {
  const editando = llave !== undefined;
  const aplicables = campos.filter((c) => !(editando && c.soloAlta));

  const [valores, setValores] = useState<Record<string, string>>(() =>
    Object.fromEntries(aplicables.map((c) => [c.nombre, valorInicial(c, actual)])),
  );
  const [listas, setListas] = useState<Record<string, Opcion[]>>({});
  /* Por ruta: si el catalogo viene cortado y cuantos hay en total. */
  const [parciales, setParciales] = useState<Record<string, { parcial: boolean; total: number }>>(
    {},
  );
  /* Lo escrito en el buscador de cada catalogo cortado, y lo que contesto. */
  const [buscando, setBuscando] = useState<Record<string, string>>({});
  const [hallados, setHallados] = useState<Record<string, Opcion[] | undefined>>({});
  const [error, setError] = useState<unknown>(null);
  const [guardando, setGuardando] = useState(false);
  const idBase = useId();

  /* Los catalogos se piden al abrir. `opciones()` comparte la promesa, asi
     que un formulario con cinco selectores de la misma entidad hace una sola
     peticion. */
  /* Las rutas de catalogo se calculan de la declaracion, que no cambia
     mientras el panel esta abierto. Se memoiza por su contenido para que el
     efecto dependa de algo estable en vez de de un arreglo nuevo por render. */
  const rutasCatalogo = useMemo(
    () => [...new Set(campos.filter((c) => c.catalogo).map((c) => c.catalogo as string))].sort(),
    [campos],
  );

  useEffect(() => {
    let vigente = true;
    void Promise.all(rutasCatalogo.map(async (r) => [r, await catalogo(r)] as const)).then(
      (pares) => {
        if (!vigente) return;
        setListas(Object.fromEntries(pares.map(([r, c]) => [r, c.lista])));
        /* Que catalogos vienen CORTADOS. El motor topa en 200, asi que uno
           mas grande llega incompleto y su selector ofreceria las primeras
           200 por orden alfabetico sin decirlo: con las 2815 ciudades de la
           republica, no habria forma de elegir Zacatecas y pareceria que esa
           ciudad no existe. */
        setParciales(
          Object.fromEntries(pares.map(([r, c]) => [r, { parcial: c.parcial, total: c.total }])),
        );
      },
    );
    return () => {
      vigente = false;
    };
  }, [rutasCatalogo]);

  /**
   * Busca en un catalogo cortado, contra el servidor.
   *
   * Reemplaza lo que ofrece ESE selector. No se toca la cache de
   * `opciones()`: lo buscado es para este campo y este momento, y meterlo en
   * la cache dejaria a los demas selectores de la pantalla viendo nada mas
   * los resultados de una busqueda ajena.
   */
  const buscar = useCallback((ruta: string, q: string) => {
    setBuscando((b) => ({ ...b, [ruta]: q }));
    if (q.trim().length < 2) {
      setHallados((h) => ({ ...h, [ruta]: undefined }));
      return;
    }
    void buscarOpciones(ruta, q.trim())
      .then((r) => setHallados((h) => ({ ...h, [ruta]: r })))
      .catch(() => setHallados((h) => ({ ...h, [ruta]: [] })));
  }, []);

  /** Lo que se manda: tipado, sin cadenas vacias, el dinero en centavos. */
  function cuerpo(): Record<string, unknown> {
    const salida: Record<string, unknown> = {};
    for (const c of aplicables) {
      const v = valores[c.nombre] ?? '';
      /* Un campo vacio se OMITE en vez de mandarse como "". El esquema del
         servidor acepta que el campo no venga, no que venga vacio, y mandar
         "" borraria un dato existente al editar. */
      if (v === '') continue;
      if (c.tipo === 'numero') salida[c.nombre] = Number(v);
      else if (c.tipo === 'dinero') salida[c.nombre] = Math.round(Number(v) * 100);
      else if (c.tipo === 'si-no') salida[c.nombre] = v === 'si';
      else salida[c.nombre] = v;
    }
    return salida;
  }

  async function enviar(ev: React.FormEvent): Promise<void> {
    ev.preventDefault();
    setGuardando(true);
    setError(null);
    try {
      if (editando) await api.ajustar(`/v1/${ruta}/${encodeURIComponent(llave)}`, cuerpo());
      else await api.enviar(`/v1/${ruta}`, cuerpo());
      /* Esta entidad puede ser catalogo de otra: dar de alta un cliente y no
         verlo en el selector de sitios hasta recargar la pagina es el tipo de
         detalle que hace que la gente deje de confiar en la pantalla. */
      olvidarOpciones(ruta);
      alGuardar();
    } catch (e) {
      setError(e);
    } finally {
      setGuardando(false);
    }
  }

  /* Los problemas que el servidor atribuyo a un campo, por campo. Zod los
     manda en `detalles.problemas` con la ruta del campo. */
  const problemas = new Map<string, string>();
  if (error instanceof ErrorApi) {
    const lista = error.detalles?.['problemas'];
    if (Array.isArray(lista)) {
      for (const p of lista as { path?: unknown[]; message?: string }[]) {
        const campo = aTexto(p.path?.[0]);
        if (campo && p.message) problemas.set(campo, p.message);
      }
    }
  }

  return (
    <aside
      className="panel-lado"
      role="dialog"
      aria-modal="true"
      aria-label={titulo}
      data-prueba="formulario"
    >
      <header className="panel-h">
        <h2>{titulo}</h2>
        <button type="button" className="icobtn" onClick={alCerrar} aria-label="Cerrar">
          ✕
        </button>
      </header>

      <form onSubmit={(ev) => void enviar(ev)} className="panel-b">
        {/* El error general va arriba, con su codigo. Los de campo van
            pegados a su campo: es donde la persona esta mirando. */}
        {error !== null && problemas.size === 0 && <AvisoError error={error} />}
        {error !== null && problemas.size > 0 && (
          <div className="warnbox" role="alert">
            Revisa los {problemas.size === 1 ? 'campo marcado' : 'campos marcados'} abajo.
          </div>
        )}

        <div className="grid r2 campos">
          {aplicables.map((c) => {
            const id = `${idBase}-${c.nombre}`;
            const mal = problemas.get(c.nombre);
            const lista = c.catalogo ? listas[c.catalogo] : undefined;
            /* Lo que el selector ofrece.
            
               Si hay una busqueda en curso manda lo hallado, que es lo que la
               persona esta mirando; si no, la primera pagina.
            
               Y un catalogo desactivado se sigue leyendo en los registros
               viejos pero ya no se ofrece, SALVO que sea justo el valor que
               este registro trae: filtrarlo tambien ahi haria que editar otro
               campo lo borrara en silencio. */
            const ofrecidas = (
              (c.catalogo ? hallados[c.catalogo] : undefined) ??
              lista ??
              []
            ).filter((o) => o.activo || o.id === valores[c.nombre]);

            return (
              <div
                key={c.nombre}
                className={`campo${c.ancho || c.tipo === 'texto-largo' ? ' ancho' : ''}${mal ? ' mal' : ''}`}
              >
                <label htmlFor={id}>
                  {c.etiqueta}
                  {c.requerido && (
                    <>
                      {' '}
                      <span aria-hidden="true">*</span>
                      <span className="visualmente-oculto">(obligatorio)</span>
                    </>
                  )}
                </label>

                {c.tipo === 'texto-largo' ? (
                  <textarea
                    id={id}
                    value={valores[c.nombre] ?? ''}
                    onChange={(ev) => setValores({ ...valores, [c.nombre]: ev.target.value })}
                    {...(c.requerido ? { required: true } : {})}
                    {...(mal ? { 'aria-invalid': true, 'aria-describedby': `${id}-mal` } : {})}
                  />
                ) : c.tipo === 'lista' || c.tipo === 'catalogo' ? (
                  <>
                    {/* El buscador aparece SOLO si el catalogo viene cortado,
                        y eso lo decide `meta.total` del servidor: ningun campo
                        declara nada. Asi, el dia que un catalogo pase de 200
                        registros su selector se arregla solo, y uno de ocho no
                        manda a nadie a buscar en una lista de ocho.

                        Un buscador MAS un selector, en vez de un combobox
                        propio: un combobox a mano es la forma clasica de
                        reprobar axe —que es criterio de cierre aqui— y esto es
                        dos controles nativos, cada uno con su etiqueta. */}
                    {c.tipo === 'catalogo' && c.catalogo && parciales[c.catalogo]?.parcial && (
                      <>
                        <input
                          type="search"
                          id={`${id}-busca`}
                          value={buscando[c.catalogo] ?? ''}
                          onChange={(ev) => buscar(c.catalogo as string, ev.target.value)}
                          placeholder="Escribe para buscar…"
                          aria-label={`Buscar en ${c.etiqueta.toLowerCase()}`}
                          aria-describedby={`${id}-corte`}
                          data-prueba={`busca-${c.catalogo}`}
                        />
                        <span className="ayuda" id={`${id}-corte`}>
                          {parciales[c.catalogo]?.total} registros en total; el selector muestra los
                          primeros. Escribe al menos dos letras para buscar entre todos.
                        </span>
                      </>
                    )}
                    <select
                      id={id}
                      value={valores[c.nombre] ?? ''}
                      onChange={(ev) => setValores({ ...valores, [c.nombre]: ev.target.value })}
                      {...(c.requerido ? { required: true } : {})}
                      {...(mal ? { 'aria-invalid': true, 'aria-describedby': `${id}-mal` } : {})}
                    >
                      <option value="">
                        {c.catalogo && lista === undefined ? 'Cargando…' : '— elegir —'}
                      </option>
                      {c.tipo === 'lista'
                        ? (c.valores ?? []).map((v) => (
                            <option key={v.valor} value={v.valor}>
                              {v.etiqueta}
                            </option>
                          ))
                        : ofrecidas.map((o) => (
                            <option key={o.id} value={o.id}>
                              {o.etiqueta}
                            </option>
                          ))}
                    </select>
                  </>
                ) : c.tipo === 'si-no' ? (
                  <select
                    id={id}
                    value={valores[c.nombre] ?? ''}
                    onChange={(ev) => setValores({ ...valores, [c.nombre]: ev.target.value })}
                    {...(mal ? { 'aria-invalid': true, 'aria-describedby': `${id}-mal` } : {})}
                  >
                    <option value="">— sin definir —</option>
                    <option value="si">Si</option>
                    <option value="no">No</option>
                  </select>
                ) : (
                  <input
                    id={id}
                    type={
                      c.tipo === 'fecha'
                        ? 'date'
                        : c.tipo === 'hora'
                          ? 'time'
                          : c.tipo === 'numero' || c.tipo === 'dinero'
                            ? 'number'
                            : 'text'
                    }
                    value={valores[c.nombre] ?? ''}
                    onChange={(ev) => setValores({ ...valores, [c.nombre]: ev.target.value })}
                    {...(c.requerido ? { required: true } : {})}
                    {...(c.min !== undefined ? { min: c.min } : {})}
                    {...(c.max !== undefined ? { max: c.max } : {})}
                    {...(c.patron ? { pattern: c.patron } : {})}
                    {...(c.tipo === 'dinero' ? { step: '0.01' } : {})}
                    {...(mal ? { 'aria-invalid': true, 'aria-describedby': `${id}-mal` } : {})}
                  />
                )}

                {mal && (
                  <span className="ayuda malo" id={`${id}-mal`}>
                    {mal}
                  </span>
                )}
                {!mal && c.ayuda && <span className="ayuda">{c.ayuda}</span>}
              </div>
            );
          })}
        </div>

        {extra}

        <footer className="panel-f">
          <button type="button" className="btn ghost" onClick={alCerrar}>
            Cancelar
          </button>
          <button type="submit" className="btn pri" disabled={guardando} data-prueba="guardar">
            {guardando ? 'Guardando…' : editando ? 'Guardar cambios' : 'Dar de alta'}
          </button>
        </footer>
      </form>
    </aside>
  );
}
